import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useLazyQuery } from '@apollo/client';

import { getClient } from '../../apollo';
import loginPhoneExists from '../../graphql/user/loginPhoneExists';

/**
 * GraphQL query response data structure
 */
interface QueryData {
  /**
   * Whether the phone number already exists in the system
   */
  loginPhoneExists: boolean;
}

/**
 * GraphQL query variables structure
 */
interface QueryVariables {
  /**
   * Phone number to check for existence
   */
  phoneNumber: string;
}

/**
 * Return type for the usePhoneNumberExists hook
 */
export interface UsePhoneNumberExistsReturn {
  /**
   * Async function to check if a phone number exists
   * @param phoneNumber - The phone number to validate
   * @returns Promise resolving to `true` if phone number doesn't exist (available), `false` if it exists
   * @throws Error if the query fails
   */
  checkPhoneNumberExists: (phoneNumber: string) => Promise<boolean>;
  /**
   * Whether the checked phone number exists in the system
   */
  phoneNumberExists: boolean | null;
  /**
   * Error message if validation failed or phone number already exists, null otherwise
   */
  error: string | null;
  /**
   * Whether the phone number check is currently in progress
   */
  isLoading: boolean;
}

/**
 * Custom hook for checking if a phone number already exists in the system
 * 
 * Provides an async function to validate phone number availability and manages
 * the loading state, error messages, and existence status.
 * 
 * @returns Object containing validation function, existence status, error message, and loading state
 * 
 * @example
 * ```tsx
 * const { checkPhoneNumberExists, phoneNumberExists, error, isLoading } = usePhoneNumberExists();
 * 
 * const handleSubmit = async (phoneNumber: string) => {
 *   try {
 *     const isAvailable = await checkPhoneNumberExists(phoneNumber);
 *     if (isAvailable) {
 *       // Phone number is available, proceed with registration
 *     }
 *   } catch (err) {
 *     // Handle validation error
 *   }
 * };
 * 
 * return (
 *   <div>
 *     {error && <ErrorMessage>{error}</ErrorMessage>}
 *     <input 
 *       disabled={isLoading}
 *       placeholder="Phone number"
 *     />
 *     {phoneNumberExists && <span>This number is already registered</span>}
 *   </div>
 * );
 * ```
 */
export function usePhoneNumberExists(): UsePhoneNumberExistsReturn {
  const [phoneNumberExists, setPhoneNumberExists] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();
  
  const [checkPhoneNumberQuery, { loading: isLoading, error: apolloError }] =
    useLazyQuery<QueryData, QueryVariables>(loginPhoneExists, {
      client: getClient(),
      fetchPolicy: 'network-only',
    });

  const checkPhoneNumberExists = useCallback(
    async (phoneNumber: string): Promise<boolean> => {
      setError(null);
      setPhoneNumberExists(null);

      try {
        const result = await checkPhoneNumberQuery({
          variables: { phoneNumber },
        });

        if (result.error) {
          throw result.error;
        }

        const exists = result.data?.loginPhoneExists ?? false;

        setPhoneNumberExists(exists);
        setError(exists ? t('web.onboarding.userProfile.phoneAlreadyExistsError') : null);

        return !exists; // Return true if phone doesn't exist
      } catch (err) {
        console.error('Error checking phone number existence:', err);
        setError(t('web.onboarding.userProfile.unknownError.message'));
        setPhoneNumberExists(null);

        throw err;
      }
    },
    [checkPhoneNumberQuery, t]
  );

  // Derive error from Apollo error if no local error is set
  const displayError = error || (apolloError ? t('web.onboarding.userProfile.phoneValidationFailedError') : null);

  return {
    checkPhoneNumberExists,
    phoneNumberExists,
    error: displayError,
    isLoading,
  };
}
